import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LinkIcon from '@mui/icons-material/Link';
import NotesIcon from '@mui/icons-material/Notes';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import RuleIcon from '@mui/icons-material/Rule';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import { themeBackgrounds } from '../../theme/theme';
import AIAssistantSection from './AIAssistantSection';
import {
  aiChatBotKnowledgeService,
  BotContext,
  buildInstructionsPayload,
  getKnowledgeApiError,
  InstructionMode,
  KnowledgeBase,
  resolveInstructionMode,
} from '../../services/aiChatBotKnowledgeService';

type AISection = 'instructions' | 'knowledge' | 'assistant';
type KnowledgeTab = 'files' | 'text' | 'links';
type DeleteTarget = { type: 'file' | 'text' | 'link'; id: string; label: string };

interface CreateAIChatBotProps {
  bot: { bot_id: string; bot_name: string };
  onBack: () => void;
}

const ALLOWED_EXTENSIONS = ['pdf', 'docx', 'txt'];
const MAX_FILE_SIZE_MB = 10;
const MAX_TEXT_TITLE_CHARS = 200;
const MAX_TEXT_CONTENT_CHARS = 20000;

const getExtension = (name: string) => name.split('.').pop()?.toLowerCase() || '';

const formatFileSize = (bytes: number) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isValidUrl = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const fileIcon = (name: string) => {
  const ext = getExtension(name);
  if (ext === 'pdf') return <PictureAsPdfIcon sx={{ color: '#d32f2f' }} />;
  if (ext === 'docx') return <DescriptionIcon sx={{ color: '#1976d2' }} />;
  return <InsertDriveFileIcon sx={{ color: 'text.secondary' }} />;
};

const processingChip = (status?: string) => {
  const value = (status || '').toLowerCase();
  if (['completed', 'processed', 'ready', 'success', 'indexed'].includes(value)) {
    return <Chip size="small" label="Ready" color="success" variant="outlined" />;
  }
  if (['failed', 'error'].includes(value)) {
    return <Chip size="small" label="Failed" color="error" variant="outlined" />;
  }
  return <Chip size="small" label={value === 'pending' || !value ? 'Processing' : status} color="warning" variant="outlined" />;
};

const cleanPoints = (points: string[]) => points.map((point) => point.trim()).filter(Boolean);

const primaryButtonSx = {
  background: themeBackgrounds.default,
  color: '#ffffff',
  '&:hover': { background: themeBackgrounds.default, opacity: 0.9 },
  '&.Mui-disabled': { background: 'grey', color: '#ffffff' },
};

const sectionToggleSx = {
  px: 3,
  py: 1.25,
  textTransform: 'none',
  fontWeight: 600,
  '&.Mui-selected': {
    background: themeBackgrounds.default,
    color: '#ffffff',
    '&:hover': {
      background: themeBackgrounds.default,
      opacity: 0.9,
    },
  },
};

const CreateAIChatBot: React.FC<CreateAIChatBotProps> = ({ bot, onBack }) => {
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const appId = useSelector((state: any) => state.applicationDetails.app_id);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ctx: BotContext = useMemo(() => ({
    organization_id: userDetails?.organization_id || '',
    application_id: appId || '',
    bot_id: bot.bot_id,
  }), [userDetails?.organization_id, appId, bot.bot_id]);

  const [activeSection, setActiveSection] = useState<AISection>('instructions');
  const [knowledgeTab, setKnowledgeTab] = useState<KnowledgeTab>('files');

  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [instructionMode, setInstructionMode] = useState<InstructionMode>('paragraph');
  const [instructionParagraph, setInstructionParagraph] = useState('');
  const [instructionPoints, setInstructionPoints] = useState<string[]>(['']);
  const [isSavingInstructions, setIsSavingInstructions] = useState(false);

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [isSavingText, setIsSavingText] = useState(false);

  const [linkInput, setLinkInput] = useState('');
  const [linkError, setLinkError] = useState('');
  const [isSavingLink, setIsSavingLink] = useState(false);

  const [busyItemId, setBusyItemId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  const syncInstructions = (kb: KnowledgeBase) => {
    const saved = kb.instructions;
    const savedMode = saved?.mode === 'both' || saved?.mode === 'points' || saved?.mode === 'paragraph'
      ? saved.mode
      : resolveInstructionMode(saved?.paragraph || '', saved?.points || []);
    setInstructionMode(savedMode);
    setInstructionParagraph(saved?.paragraph || '');
    setInstructionPoints(saved?.points?.length ? saved.points : ['']);
  };

  const loadKnowledgeBase = useCallback(async () => {
    if (!ctx.organization_id || !ctx.application_id || !ctx.bot_id) {
      setIsLoading(false);
      setLoadError('Organization or application details are missing.');
      return;
    }
    setIsLoading(true);
    setLoadError('');
    try {
      const { data } = await aiChatBotKnowledgeService.getKnowledgeBase(ctx);
      setKnowledgeBase(data.knowledgeBase);
      syncInstructions(data.knowledgeBase);
    } catch (error) {
      setLoadError(getKnowledgeApiError(error, 'Failed to load knowledge base.'));
    } finally {
      setIsLoading(false);
    }
  }, [ctx]);

  useEffect(() => {
    loadKnowledgeBase();
  }, [loadKnowledgeBase]);

  const filledPoints = cleanPoints(instructionPoints);
  const hasParagraph = Boolean(instructionParagraph.trim());
  const hasInstructions = hasParagraph || filledPoints.length > 0;
  const resolvedMode = resolveInstructionMode(instructionParagraph, instructionPoints);

  const savedInstructions = knowledgeBase?.instructions;
  const isInstructionsDirty = !savedInstructions
    || savedInstructions.mode !== resolvedMode
    || (savedInstructions.paragraph || '') !== instructionParagraph.trim()
    || JSON.stringify(cleanPoints(savedInstructions.points || [])) !== JSON.stringify(filledPoints);

  const files = knowledgeBase?.files || [];
  const texts = knowledgeBase?.texts || [];
  const links = knowledgeBase?.links || [];
  const limits = knowledgeBase?.limits;
  const sourceCount = files.length + texts.length + links.length;
  const isAiActive = knowledgeBase?.status === 'Active';
  const canActivate = hasInstructions && sourceCount > 0;

  const instructionsPayload = () => buildInstructionsPayload(instructionParagraph, instructionPoints);

  const handleSaveInstructions = async () => {
    if (!hasInstructions) {
      toast.error('Please add at least one instruction.', { position: 'top-right' });
      return;
    }
    setIsSavingInstructions(true);
    try {
      const { data, message } = await aiChatBotKnowledgeService.saveInstructions(ctx, instructionsPayload());
      setKnowledgeBase(data.knowledgeBase);
      syncInstructions(data.knowledgeBase);
      toast.success(message || 'Instructions saved successfully.', { position: 'top-right' });
    } catch (error) {
      toast.error(getKnowledgeApiError(error, 'Failed to save instructions.'), { position: 'top-right' });
    } finally {
      setIsSavingInstructions(false);
    }
  };

  const updatePoint = (index: number, value: string) => {
    setInstructionPoints((prev) => prev.map((point, i) => (i === index ? value : point)));
  };

  const addPoint = () => setInstructionPoints((prev) => [...prev, '']);

  const removePoint = (index: number) => {
    setInstructionPoints((prev) => (prev.length === 1 ? [''] : prev.filter((_, i) => i !== index)));
  };

  const addPendingFiles = (incoming: FileList | File[]) => {
    const accepted: File[] = [];
    Array.from(incoming).forEach((file) => {
      if (!ALLOWED_EXTENSIONS.includes(getExtension(file.name))) {
        toast.error(`${file.name}: only PDF, DOCX and TXT files are supported.`, { position: 'top-right' });
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast.error(`${file.name}: file is larger than ${MAX_FILE_SIZE_MB} MB.`, { position: 'top-right' });
        return;
      }
      const isDuplicate = pendingFiles.some((existing) => existing.name === file.name && existing.size === file.size);
      if (!isDuplicate) {
        accepted.push(file);
      }
    });
    if (accepted.length) {
      setPendingFiles((prev) => [...prev, ...accepted]);
    }
  };

  const handleUploadFiles = async () => {
    if (!pendingFiles.length) return;
    if (limits && files.length + pendingFiles.length > limits.files) {
      toast.error(`A maximum of ${limits.files} files is allowed per bot.`, { position: 'top-right' });
      return;
    }
    setIsUploading(true);
    try {
      const { data, message } = await aiChatBotKnowledgeService.uploadFiles(ctx, pendingFiles);
      setKnowledgeBase(data.knowledgeBase);
      setPendingFiles([]);
      toast.success(message || 'Files uploaded successfully.', { position: 'top-right' });
    } catch (error: any) {
      const status = error?.response?.data?.status || error?.response?.status;
      const detail = getKnowledgeApiError(error, 'Failed to upload files.');
      toast.error(
        status === 502 || status === 503
          ? `${detail}. Confirm S3 storage is saved in Application Settings → Storage.`
          : detail,
        { position: 'top-right' }
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenFile = async (fileId: string, disposition: 'inline' | 'attachment') => {
    // Opened synchronously so the popup blocker treats it as user-initiated.
    const newWindow = disposition === 'inline' ? window.open('', '_blank') : null;
    setBusyItemId(fileId);
    try {
      const { data } = await aiChatBotKnowledgeService.getFileUrl(ctx, fileId, disposition);
      if (disposition === 'attachment') {
        const anchor = document.createElement('a');
        anchor.href = data.url;
        anchor.rel = 'noopener';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } else if (newWindow) {
        newWindow.opener = null;
        newWindow.location.href = data.url;
      } else {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      newWindow?.close();
      toast.error(getKnowledgeApiError(error, 'Failed to open file.'), { position: 'top-right' });
    } finally {
      setBusyItemId(null);
    }
  };

  const resetTextForm = () => {
    setTextTitle('');
    setTextContent('');
    setEditingTextId(null);
  };

  const handleSaveText = async () => {
    const title = textTitle.trim();
    const content = textContent.trim();
    if (!title || !content) {
      toast.error('Please enter both a title and content.', { position: 'top-right' });
      return;
    }
    setIsSavingText(true);
    try {
      const { data, message } = editingTextId
        ? await aiChatBotKnowledgeService.updateText(ctx, editingTextId, title, content)
        : await aiChatBotKnowledgeService.addText(ctx, title, content);
      setKnowledgeBase(data.knowledgeBase);
      resetTextForm();
      toast.success(message || (editingTextId ? 'Text updated successfully.' : 'Text added successfully.'), {
        position: 'top-right',
      });
    } catch (error) {
      toast.error(getKnowledgeApiError(error, 'Failed to save text.'), { position: 'top-right' });
    } finally {
      setIsSavingText(false);
    }
  };

  const startEditText = (textId: string) => {
    const entry = texts.find((item) => item.text_id === textId);
    if (!entry) return;
    setEditingTextId(entry.text_id);
    setTextTitle(entry.title);
    setTextContent(entry.content);
  };

  const handleAddLink = async () => {
    const url = linkInput.trim();
    if (!isValidUrl(url)) {
      setLinkError('Enter a valid URL starting with http:// or https://');
      return;
    }
    if (links.some((link) => link.url === url || link.url === `${url}/`)) {
      setLinkError('This link has already been added.');
      return;
    }
    setIsSavingLink(true);
    try {
      const { data, message } = await aiChatBotKnowledgeService.addLink(ctx, url);
      setKnowledgeBase(data.knowledgeBase);
      setLinkInput('');
      setLinkError('');
      toast.success(message || 'Link added successfully.', { position: 'top-right' });
    } catch (error) {
      setLinkError(getKnowledgeApiError(error, 'Failed to add link.'));
    } finally {
      setIsSavingLink(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const { id, type } = deleteTarget;
      const result = type === 'file'
        ? await aiChatBotKnowledgeService.deleteFile(ctx, id)
        : type === 'text'
          ? await aiChatBotKnowledgeService.deleteText(ctx, id)
          : await aiChatBotKnowledgeService.deleteLink(ctx, id);
      setKnowledgeBase(result.data.knowledgeBase);
      if (type === 'text' && editingTextId === id) {
        resetTextForm();
      }
      toast.success(result.message || 'Deleted successfully.', { position: 'top-right' });
      setDeleteTarget(null);
    } catch (error) {
      toast.error(getKnowledgeApiError(error, 'Failed to delete.'), { position: 'top-right' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleActivate = async () => {
    if (!canActivate) return;
    setIsActivating(true);
    try {
      const { data, message } = await aiChatBotKnowledgeService.activate(ctx, instructionsPayload());
      setKnowledgeBase(data.knowledgeBase);
      syncInstructions(data.knowledgeBase);
      toast.success(message || 'AI chat bot activated successfully.', { position: 'top-right' });
    } catch (error) {
      toast.error(getKnowledgeApiError(error, 'Failed to activate AI chat bot.'), { position: 'top-right' });
    } finally {
      setIsActivating(false);
    }
  };

  const activateHint = !hasInstructions
    ? 'Add instructions before activating the AI bot'
    : sourceCount === 0
      ? 'Add at least one file, text or link to the knowledge base'
      : 'Activate the AI bot';

  const limitLabel = (count: number, max?: number) => (max ? `${count} / ${max}` : `${count}`);

  const renderEmpty = (message: string) => (
    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
      {message}
    </Typography>
  );

  const renderIconAction = (title: string, icon: React.ReactNode, onClick: () => void, disabled = false) => (
    <Tooltip title={title}>
      <span>
        <IconButton size="small" onClick={onClick} disabled={disabled} aria-label={title}>
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  const renderFilesTab = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload documents the AI chat bot can learn from. Supported formats are PDF, DOCX and TXT (max {MAX_FILE_SIZE_MB} MB each).
      </Typography>

      <Paper
        variant="outlined"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          if (!isUploading) addPendingFiles(event.dataTransfer.files);
        }}
        sx={{
          p: 4,
          mb: 3,
          textAlign: 'center',
          borderStyle: 'dashed',
          borderColor: '#90caf9',
          backgroundColor: '#f8fbff',
        }}
      >
        <UploadFileIcon sx={{ fontSize: 42, color: '#007aff', mb: 1 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Choose or drop files
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          PDF, DOCX, TXT
        </Typography>
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          Select Files
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.txt"
          style={{ display: 'none' }}
          onChange={(event) => {
            if (event.target.files) {
              addPendingFiles(event.target.files);
            }
            event.target.value = '';
          }}
        />
      </Paper>

      {pendingFiles.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Ready to upload ({pendingFiles.length})
          </Typography>
          <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
            {pendingFiles.map((file, index) => (
              <ListItem
                key={`${file.name}-${file.size}`}
                divider={index < pendingFiles.length - 1}
                secondaryAction={renderIconAction(
                  `Remove ${file.name}`,
                  <CloseIcon fontSize="small" />,
                  () => setPendingFiles((prev) => prev.filter((_, i) => i !== index)),
                  isUploading
                )}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{fileIcon(file.name)}</ListItemIcon>
                <ListItemText primary={file.name} secondary={formatFileSize(file.size)} />
              </ListItem>
            ))}
          </List>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => setPendingFiles([])} disabled={isUploading}>
              Clear
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadFiles}
              disabled={isUploading}
              startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              sx={primaryButtonSx}
            >
              {isUploading ? 'Uploading...' : `Upload ${pendingFiles.length} File${pendingFiles.length === 1 ? '' : 's'}`}
            </Button>
          </Box>
        </Box>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Saved files ({limitLabel(files.length, limits?.files)})
      </Typography>
      {files.length === 0 ? renderEmpty('No files uploaded yet.') : (
        <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {files.map((file, index) => (
            <ListItem
              key={file.file_id}
              divider={index < files.length - 1}
              sx={{ pr: 18 }}
              secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {busyItemId === file.file_id && <CircularProgress size={16} sx={{ mr: 1 }} />}
                  {renderIconAction('View', <VisibilityIcon fontSize="small" />, () => handleOpenFile(file.file_id, 'inline'), busyItemId === file.file_id)}
                  {renderIconAction('Download', <DownloadIcon fontSize="small" />, () => handleOpenFile(file.file_id, 'attachment'), busyItemId === file.file_id)}
                  {renderIconAction(
                    'Delete',
                    <DeleteOutlineIcon fontSize="small" />,
                    () => setDeleteTarget({ type: 'file', id: file.file_id, label: file.original_filename })
                  )}
                </Box>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{fileIcon(file.original_filename)}</ListItemIcon>
              <ListItemText
                primary={file.original_filename}
                secondary={
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    {formatFileSize(file.file_size)}
                    {processingChip(file.processing_status)}
                  </Box>
                }
                slotProps={{ secondary: { component: 'div' } }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </>
  );

  const renderTextTab = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add information as text. Give each entry a title and write the content as a paragraph.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderColor: editingTextId ? 'primary.main' : 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {editingTextId ? 'Edit text' : 'Add new text'}
        </Typography>
        <TextField
          label="Title"
          placeholder="Example: Refund policy"
          fullWidth
          value={textTitle}
          onChange={(event) => setTextTitle(event.target.value)}
          disabled={isSavingText}
          slotProps={{ htmlInput: { maxLength: MAX_TEXT_TITLE_CHARS } }}
          sx={{ mb: 3 }}
        />
        <TextField
          label="Content"
          placeholder="Write the information the AI chat bot should know about this topic."
          fullWidth
          multiline
          minRows={5}
          value={textContent}
          onChange={(event) => setTextContent(event.target.value)}
          disabled={isSavingText}
          helperText={`${textContent.length} / ${MAX_TEXT_CONTENT_CHARS}`}
          slotProps={{ htmlInput: { maxLength: MAX_TEXT_CONTENT_CHARS } }}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {(editingTextId || textTitle || textContent) && (
            <Button variant="outlined" onClick={resetTextForm} disabled={isSavingText}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSaveText}
            disabled={!textTitle.trim() || !textContent.trim() || isSavingText}
            startIcon={isSavingText ? <CircularProgress size={18} color="inherit" /> : editingTextId ? <SaveIcon /> : <AddIcon />}
            sx={primaryButtonSx}
          >
            {isSavingText ? 'Saving...' : editingTextId ? 'Update Text' : 'Save Text'}
          </Button>
        </Box>
      </Paper>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Saved text ({limitLabel(texts.length, limits?.texts)})
      </Typography>
      {texts.length === 0 ? renderEmpty('No text added yet.') : (
        <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {texts.map((entry, index) => (
            <ListItem
              key={entry.text_id}
              divider={index < texts.length - 1}
              alignItems="flex-start"
              sx={{
                pr: 12,
                backgroundColor: editingTextId === entry.text_id ? 'rgba(25, 118, 210, 0.06)' : 'transparent',
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {renderIconAction('Edit', <EditIcon fontSize="small" />, () => startEditText(entry.text_id), isSavingText)}
                  {renderIconAction(
                    'Delete',
                    <DeleteOutlineIcon fontSize="small" />,
                    () => setDeleteTarget({ type: 'text', id: entry.text_id, label: entry.title })
                  )}
                </Box>
              }
            >
              <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                <NotesIcon sx={{ color: '#007aff' }} />
              </ListItemIcon>
              <ListItemText
                primary={entry.title}
                secondary={entry.content}
                slotProps={{
                  secondary: {
                    sx: {
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    },
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </>
  );

  const renderLinksTab = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add web pages the AI chat bot should read content from.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 3 }}>
        <TextField
          label="Link"
          placeholder="https://example.com/help-article"
          fullWidth
          value={linkInput}
          error={Boolean(linkError)}
          helperText={linkError || 'URL must start with http:// or https://'}
          disabled={isSavingLink}
          onChange={(event) => {
            setLinkInput(event.target.value);
            if (linkError) setLinkError('');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleAddLink();
            }
          }}
        />
        <Button
          variant="contained"
          startIcon={isSavingLink ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
          onClick={handleAddLink}
          disabled={!linkInput.trim() || isSavingLink}
          sx={{ ...primaryButtonSx, height: 56, flexShrink: 0 }}
        >
          {isSavingLink ? 'Saving...' : 'Save Link'}
        </Button>
      </Box>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Saved links ({limitLabel(links.length, limits?.links)})
      </Typography>
      {links.length === 0 ? renderEmpty('No links added yet.') : (
        <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {links.map((link, index) => (
            <ListItem
              key={link.link_id}
              divider={index < links.length - 1}
              sx={{ pr: 12 }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {renderIconAction('Open link', <OpenInNewIcon fontSize="small" />, () => window.open(link.url, '_blank', 'noopener,noreferrer'))}
                  {renderIconAction(
                    'Delete',
                    <DeleteOutlineIcon fontSize="small" />,
                    () => setDeleteTarget({ type: 'link', id: link.link_id, label: link.url })
                  )}
                </Box>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <LinkIcon sx={{ color: '#007aff' }} />
              </ListItemIcon>
              <ListItemText
                primary={link.url}
                secondary={processingChip(link.processing_status)}
                slotProps={{
                  primary: { sx: { wordBreak: 'break-all' } },
                  secondary: { component: 'div', sx: { mt: 0.5 } },
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </>
  );

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <PageHeader
        heading="AI Chat Bot Settings"
        subheading={`Configure instructions, knowledge base and AI assistant for "${bot.bot_name}".`}
        variant="gradient"
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>

        <ToggleButtonGroup
          exclusive
          value={activeSection}
          onChange={(_, value: AISection | null) => value && setActiveSection(value)}
          sx={{
            backgroundColor: '#fff',
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0, 122, 255, 0.08)',
            flexWrap: 'wrap',
          }}
        >
          <ToggleButton value="instructions" sx={sectionToggleSx}>
            <RuleIcon sx={{ mr: 1, fontSize: 18 }} />
            Instructions
          </ToggleButton>
          <ToggleButton value="knowledge" sx={sectionToggleSx}>
            <LibraryBooksIcon sx={{ mr: 1, fontSize: 18 }} />
            Knowledge Base
          </ToggleButton>
          <ToggleButton value="assistant" sx={sectionToggleSx}>
            <RecordVoiceOverIcon sx={{ mr: 1, fontSize: 18 }} />
            AI Assistant
          </ToggleButton>
        </ToggleButtonGroup>

        {isAiActive ? (
          <Chip
            icon={<CheckCircleIcon />}
            label="AI Bot Active"
            color="success"
            sx={{ fontWeight: 600 }}
          />
        ) : (
          <Tooltip title={activateHint}>
            <span>
              <Button
                variant="contained"
                onClick={handleActivate}
                disabled={!canActivate || isActivating || isLoading}
                startIcon={isActivating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                sx={primaryButtonSx}
              >
                {isActivating ? 'Activating...' : 'Activate AI Bot'}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {activeSection === 'assistant' ? (
        <AIAssistantSection
          key={bot.bot_id}
          organizationId={ctx.organization_id}
          applicationId={ctx.application_id}
          botId={ctx.bot_id}
          botName={bot.bot_name}
        />
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading knowledge base...</Typography>
        </Box>
      ) : loadError ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="error" sx={{ mb: 2 }}>{loadError}</Typography>
            <Button variant="outlined" onClick={loadKnowledgeBase}>Retry</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {activeSection === 'instructions' && (
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Define the rules the AI chat bot must follow when replying. Fill a paragraph, a list of points, or both.
                </Typography>

                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  value={instructionMode}
                  onChange={(_, value: InstructionMode | null) => value && setInstructionMode(value)}
                  disabled={isSavingInstructions}
                  sx={{ mb: 3 }}
                >
                  <ToggleButton value="paragraph" sx={{ textTransform: 'none', px: 2 }}>Paragraph</ToggleButton>
                  <ToggleButton value="points" sx={{ textTransform: 'none', px: 2 }}>Points</ToggleButton>
                  <ToggleButton value="both" sx={{ textTransform: 'none', px: 2 }}>Both</ToggleButton>
                </ToggleButtonGroup>

                {(instructionMode === 'paragraph' || instructionMode === 'both') && (
                  <TextField
                    label="Instructions"
                    placeholder="Example: Always reply politely. Only answer questions about our products. If you are unsure, ask the user to contact support."
                    fullWidth
                    multiline
                    minRows={instructionMode === 'both' ? 4 : 6}
                    value={instructionParagraph}
                    disabled={isSavingInstructions}
                    onChange={(event) => setInstructionParagraph(event.target.value)}
                    sx={{ mb: instructionMode === 'both' ? 3 : 0 }}
                  />
                )}

                {(instructionMode === 'points' || instructionMode === 'both') && (
                  <Box>
                    {instructionMode === 'both' && (
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Points
                      </Typography>
                    )}
                    {instructionPoints.map((point, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24, textAlign: 'right' }}>
                          {index + 1}.
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Enter a rule"
                          value={point}
                          disabled={isSavingInstructions}
                          onChange={(event) => updatePoint(index, event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              addPoint();
                            }
                          }}
                        />
                        {renderIconAction(
                          'Remove rule',
                          <DeleteOutlineIcon fontSize="small" />,
                          () => removePoint(index),
                          isSavingInstructions
                        )}
                      </Box>
                    ))}
                    <Button startIcon={<AddIcon />} onClick={addPoint} size="small" disabled={isSavingInstructions}>
                      Add rule
                    </Button>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                  {isInstructionsDirty && savedInstructions && (
                    <Button
                      variant="outlined"
                      onClick={() => knowledgeBase && syncInstructions(knowledgeBase)}
                      disabled={isSavingInstructions}
                    >
                      Reset
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleSaveInstructions}
                    disabled={!hasInstructions || !isInstructionsDirty || isSavingInstructions}
                    startIcon={isSavingInstructions ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    sx={primaryButtonSx}
                  >
                    {isSavingInstructions ? 'Saving...' : 'Save Instructions'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {activeSection === 'knowledge' && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Knowledge Base
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add files, text and links the AI chat bot can use to answer user questions.
                </Typography>

                <Tabs
                  value={knowledgeTab}
                  onChange={(_, value: KnowledgeTab) => setKnowledgeTab(value)}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: 3,
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 },
                  }}
                >
                  <Tab value="files" icon={<UploadFileIcon fontSize="small" />} iconPosition="start" label={`Files (${files.length})`} />
                  <Tab value="text" icon={<NotesIcon fontSize="small" />} iconPosition="start" label={`Text (${texts.length})`} />
                  <Tab value="links" icon={<LinkIcon fontSize="small" />} iconPosition="start" label={`Links (${links.length})`} />
                </Tabs>

                {knowledgeTab === 'files' && renderFilesTab()}
                {knowledgeTab === 'text' && renderTextTab()}
                {knowledgeTab === 'links' && renderLinksTab()}
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => !isDeleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {deleteTarget?.type}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ wordBreak: 'break-word' }}>
            Are you sure you want to delete "{deleteTarget?.label}" from the knowledge base? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={18} color="inherit" /> : <DeleteOutlineIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateAIChatBot;
